import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import styles from './CreateAccount.module.scss';
import { Input, Button, Typeahead } from '@src/components/ui';
const ROLE_CHOICES = [
    { id: 'admin', label: 'Admin', value: 'admin' },
    { id: 'dvm', label: 'DVM', value: 'dvm' },
    { id: 'owner', label: 'Owner', value: 'owner' },
    { id: 'reporter', label: 'Reporter', value: 'reporter' },
    { id: 'doctor', label: 'Doctor', value: 'doctor' },
    { id: 'other', label: 'Other', value: 'other' },
];
const CreateAccount = () => {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: '',
        role: '',
        roleInput: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasCapital: false,
        hasNumber: false,
    });
    const handleChange = (field) => (value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        // Real-time password validation
        if (field === 'password') {
            setPasswordRequirements({
                minLength: value.length >= 8,
                hasCapital: /[A-Z]/.test(value),
                hasNumber: /[0-9]/.test(value),
            });
        }
        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };
    const handleSubmit = async () => {
        setError('');
        setFieldErrors({});
        if (!form.role) {
            setFieldErrors((prev) => ({
                ...prev,
                role: 'Please select a role from the list.',
            }));
            return;
        }
        // Validate password requirements
        if (!passwordRequirements.minLength ||
            !passwordRequirements.hasCapital ||
            !passwordRequirements.hasNumber) {
            setFieldErrors((prev) => ({
                ...prev,
                password: 'Password must meet all security requirements.',
            }));
            return;
        }
        if (form.password !== form.confirmPassword) {
            setFieldErrors((prev) => ({
                ...prev,
                confirmPassword: 'Passwords do not match',
            }));
            return;
        }
        setLoading(true);
        const result = await signup({
            name: form.name,
            role: form.role,
            email: form.email,
            password: form.password,
        });
        if (result.success) {
            navigate('/account/accountSuccess');
        }
        else {
            // Parse error message into field errors
            if (result.message.includes('\n')) {
                const errors = {};
                result.message.split('\n').forEach((line) => {
                    const [field, message] = line.split(': ');
                    if (field && message) {
                        errors[field.toLowerCase()] = message;
                    }
                });
                setFieldErrors(errors);
            }
            else {
                setError(result.message || 'Could not create account');
            }
        }
        setLoading(false);
    };
    const handleRoleChange = (input) => {
        const value = typeof input === 'string' ? input : input?.target?.value ?? '';
        setForm((prev) => ({
            ...prev,
            roleInput: value,
            role: '',
        }));
        if (fieldErrors.role) {
            setFieldErrors((prev) => ({ ...prev, role: '' }));
        }
    };
    const handleRoleSelect = (selected) => {
        const isOther = selected?.id === 'other';
        setForm((prev) => ({
            ...prev,
            role: isOther ? '' : selected?.id || '',
            roleInput: selected?.label || '',
        }));
        if (fieldErrors.role) {
            setFieldErrors((prev) => ({ ...prev, role: '' }));
        }
    };
    return (_jsxs("div", { className: styles.createAccountWrapper, children: [_jsxs("div", { className: styles.headingContainer, children: [_jsx("h3", { children: "Create an Account" }), _jsx("p", { children: "Enter your name and email address, and create a password. We'll send you an email to verify your account." })] }), _jsxs("div", { className: styles.inputContainer, children: [_jsx(Input, { label: fieldErrors.name ? `Name: ${fieldErrors.name}` : 'Name', className: styles.inputItem, width: "100%", value: form.name, onChange: handleChange('name'), error: !!fieldErrors.name }), _jsx(Typeahead, { label: fieldErrors.role ? `Error: ${fieldErrors.role}` : 'Your Role at Your Clinic', placeholder: "Search for your Role...", suggestions: ROLE_CHOICES, value: form.roleInput, onChange: handleRoleChange, onSelect: handleRoleSelect, clearable: true, invalid: !!fieldErrors.role, className: styles.inputItem, inputWidth: "100%", showTopOverride: false, valueTriggerLimit: 1 }), form.roleInput === 'Other' && (_jsx(Input, { label: fieldErrors.role
                            ? `Your Role at Your Clinic: ${fieldErrors.role}`
                            : 'Please describe your role', className: styles.inputItem, width: "100%", value: form.role, onChange: handleChange('role'), error: !!fieldErrors.role })), _jsx(Input, { label: fieldErrors.email ? `Email Address: ${fieldErrors.email}` : 'Email Address', className: styles.inputItem, width: "100%", value: form.email, onChange: handleChange('email'), error: !!fieldErrors.email }), _jsx(Input, { label: fieldErrors.password ? `Password: ${fieldErrors.password}` : 'Password', type: "password", className: styles.inputItem, width: "100%", value: form.password, onChange: handleChange('password'), error: !!fieldErrors.password }), _jsxs("div", { className: styles.passwordRequirements, children: [_jsx("p", { className: styles.requirementsTitle, children: "Password must contain:" }), _jsxs("div", { className: styles.requirementsList, children: [_jsxs("div", { className: `${styles.requirement} ${passwordRequirements.minLength ? styles.valid : styles.invalid}`, children: [_jsx("span", { className: styles.checkmark, children: passwordRequirements.minLength ? '✓' : '×' }), "Minimum 8 characters"] }), _jsxs("div", { className: `${styles.requirement} ${passwordRequirements.hasCapital ? styles.valid : styles.invalid}`, children: [_jsx("span", { className: styles.checkmark, children: passwordRequirements.hasCapital ? '✓' : '×' }), "At least one capital letter"] }), _jsxs("div", { className: `${styles.requirement} ${passwordRequirements.hasNumber ? styles.valid : styles.invalid}`, children: [_jsx("span", { className: styles.checkmark, children: passwordRequirements.hasNumber ? '✓' : '×' }), "At least one number"] })] })] }), _jsx(Input, { label: fieldErrors.confirmPassword
                            ? `Re-enter Password: ${fieldErrors.confirmPassword}`
                            : 'Re-enter Password', type: "password", className: styles.inputItem, width: "100%", value: form.confirmPassword, onChange: handleChange('confirmPassword'), error: !!fieldErrors.confirmPassword }), error && _jsx("p", { className: styles.error, children: error }), _jsx(Button, { variant: "primaryV2", label: loading ? 'Creating Account...' : 'Create Account', size: "large", onClick: handleSubmit, disabled: loading })] })] }));
};
export default CreateAccount;
